import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Zap, FileText } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@core/components/ui/accordion";
import { getGuide, getGuideList, getEquivalentSlug } from "./guidesData";

export default function GuidePage() {
  const { t, i18n } = useTranslation("forms");
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const guide = slug ? getGuide(i18n.language, slug) : undefined;
  const guideList = getGuideList(i18n.language);

  // Slugs localisés : si le slug n'existe pas dans la langue courante mais
  // correspond à un guide d'une autre langue (ex. après changement de langue),
  // on redirige vers le slug équivalent au lieu d'afficher « introuvable ».
  const equivalentSlug =
    !guide && slug ? getEquivalentSlug(slug, i18n.language) : undefined;

  useEffect(() => {
    if (equivalentSlug && equivalentSlug !== slug) {
      navigate(`/guides/${equivalentSlug}`, { replace: true });
    }
  }, [equivalentSlug, slug, navigate]);

  // SEO impératif (form n'a pas de HelmetProvider) : title, meta description,
  // canonical, et JSON-LD (Article + FAQPage). Nettoyage au démontage.
  useEffect(() => {
    if (!guide) return;
    const prevTitle = document.title;
    document.title = `${guide.title} — DooQuest`;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") ?? null;
    if (metaDesc) metaDesc.setAttribute("content", guide.metaDescription);

    let canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const prevCanonical = canonical?.getAttribute("href") ?? null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      "href",
      `${window.location.origin}/guides/${guide.slug}`,
    );

    const script = document.createElement("script");
    script.id = "guide-jsonld";
    script.type = "application/ld+json";
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: guide.title,
        description: guide.metaDescription,
        inLanguage: i18n.language,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: guide.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ]);
    document.getElementById("guide-jsonld")?.remove();
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null)
        metaDesc.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null)
        canonical.setAttribute("href", prevCanonical);
      document.getElementById("guide-jsonld")?.remove();
    };
  }, [guide, i18n.language]);

  if (!guide) {
    return (
      <div className="flex min-h-screen-safe bg-[#030712] text-white">
        <ProductSidebar productType="form" />
        <div className="flex-1">
          <ProductHeader productType="form" showAuth={true} />
          <div className="flex items-center justify-center py-32">
            <p className="text-gray-400">{t("guides.notFound")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen-safe bg-[#030712] text-white">
      <ProductSidebar productType="form" />
      <div className="flex-1 min-w-0">
        <ProductHeader productType="form" showAuth={true} />

        <div className="flex">
          <aside className="hidden lg:block w-56 shrink-0 border-r border-white/5 py-8 px-4 sticky top-0 h-screen overflow-y-auto">
            <Link
              to="/guides"
              className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 hover:text-white transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              {t("guides.allGuides")}
            </Link>
            <nav className="space-y-0.5">
              {guideList.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    g.slug === guide.slug
                      ? "bg-violet-500/10 text-violet-300 font-medium"
                      : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {g.title}
                </Link>
              ))}
            </nav>
          </aside>

          <article className="flex-1 min-w-0 max-w-3xl mx-auto px-6 py-16">
            <nav className="text-xs text-gray-500 mb-8">
              <Link to="/" className="hover:text-gray-300 transition-colors">
                {t("guides.breadcrumbHome")}
              </Link>
              <span className="mx-2">·</span>
              <Link
                to="/guides"
                className="hover:text-gray-300 transition-colors"
              >
                {t("guides.breadcrumbGuides")}
              </Link>
              <span className="mx-2">·</span>
              <span className="text-gray-400">{guide.title}</span>
            </nav>

            <h1 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">
              {guide.title}
            </h1>

            <p className="text-lg text-gray-400 leading-relaxed mb-12 border-l-2 border-violet-500/40 pl-4">
              {guide.intro}
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-8">
                {t("guides.inStepsTitle")}
              </h2>
              <div className="space-y-6">
                {guide.steps.map((step, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-6 rounded-xl border border-white/5 bg-white/[0.02]"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{step.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {guide.features.length > 0 && (
              <section className="mb-12">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  {t("guides.usefulFeatures")}
                </h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {guide.features.map((f, i) => (
                    <div
                      key={i}
                      className="p-5 rounded-xl border border-violet-500/20 bg-violet-500/5"
                    >
                      <h3 className="font-semibold text-sm mb-2 text-violet-300">
                        {f.title}
                      </h3>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  <Link
                    to="/guides"
                    className="hover:text-gray-400 transition-colors underline underline-offset-2"
                  >
                    {t("guides.allGuides")}
                  </Link>
                </p>
              </section>
            )}

            <div className="my-12 p-8 rounded-2xl bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 text-center">
              <p className="text-lg font-semibold mb-2">
                {t("guides.ctaReady")}
              </p>
              <p className="text-sm text-gray-400 mb-6">
                {t("guides.ctaReadySubtitle")}
              </p>
              <Link
                to={guide.ctaHref}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 text-white font-medium rounded-xl transition-all shadow-lg shadow-violet-500/20"
              >
                <FileText className="w-4 h-4" />
                {guide.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <section>
              <h2 className="text-2xl font-bold mb-8">
                {t("guides.faqTitle")}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {guide.faq.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`q${i}`}
                    className="border border-white/5 rounded-xl px-6 bg-white/[0.01]"
                  >
                    <AccordionTrigger className="text-left hover:no-underline text-white hover:text-violet-300">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <div className="mt-16 pt-8 border-t border-white/5 flex items-center justify-between text-sm">
              <Link
                to="/guides"
                className="text-gray-500 hover:text-gray-300 transition-colors"
              >
                {t("guides.backToGuides")}
              </Link>
              <Link
                to={guide.ctaHref}
                className="text-violet-400 hover:text-violet-300 transition-colors font-medium"
              >
                {guide.ctaLabel} →
              </Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
